<template>
  <div class="product-card h-[475px] items-end">
    <div class="absolute right-[30px] top-[30px] z-[3]">
      <AddToList custom-class="w-8 h-8" :product="product" tooltip-placement="left" />
    </div>

    <div class="product-card__image relative">
      <InnoDiscountBadge :properties="product.properties!" />
      <VcImage
        :src="product.imgSrc"
        :alt="product.name"
        size-suffix="md"
        class="w-full cursor-pointer rounded-xl object-contain object-center p-2"
        lazy
        @click="linkToRoute"
      />
    </div>

    <div class="product-card__content relative flex flex-col justify-between">
      <router-link
        :to="link"
        :title="product.name"
        class="line-clamp-2 w-full text-lg font-bold text-[color:var(--color-text)]"
        @click="$emit('link-click', $event)"
      >
        {{ product.name }}
      </router-link>

      <!-- <div v-if="product.description?.content">
        {{ product.description?.content }}
      </div> -->

      <div v-if="descriptionProperty" class="line-clamp-3">
        {{ descriptionProperty }}
      </div>

      <div class="pt-2 flex flex-wrap items-end justify-between">
        <div class="mt-2 flex flex-col">
          <VcItemPrice
            v-if="isAuthenticated"
            :properties="product.properties"
            class="mb-2 text-xl"
            :value="product.price"
          />
          <InnoInStock
            :stock-type="product.sapStockStatus"
            :is-in-stock="product.availabilityData?.isInStock"
            :quantity="product.availabilityData?.availableQuantity"
          />
        </div>
        <InnoAddToCart large :product="product" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";
import { getProductRoute } from "@/core/utilities";
import { useUser } from "@/shared/account";
import { InnoAddToCart } from "@/shared/cart";
import InnoDiscountBadge from "./inno-discount-badge.vue";
import { AddToList } from "@/shared/wishlists";
import type { Product } from "@/core/api/graphql/types";
import type { PropType } from "vue";
import type { RouteLocationRaw } from "vue-router";
import InnoInStock from "./inno-in-stock.vue";

defineEmits<{ (eventName: "link-click", globalEvent: PointerEvent): void }>();

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
});

const { isAuthenticated } = useUser();

const link = computed<RouteLocationRaw>(() => getProductRoute(props.product.id, props.product.slug));
const router = useRouter();

const descriptionProperty = computed(
  () => props.product.properties.filter((item) => item.name === "omschrijving_property")[0]?.value,
);

function linkToRoute() {
  router.push(link.value);
}
</script>

<style lang="scss">
.inno-carousel {
  &__wrapper::after {
    content: "";
    background: #fff;
    width: calc(50% - 50px);
    height: 475px;
    position: absolute;
    z-index: 0;
    left: 0;
    top: 0;
    border-radius: 12px;
  }

  .swiper-slide {
    width: calc(25% - 10px);
    height: 475px;
  }

  .swiper-slide-prev {
    width: 50%;
    height: 475px;
  }

  .swiper-slide-active {
    width: 50%;
    height: 475px;
  }

  .product-card {
    background: #fff;
  }

  .swiper-slide:not(.swiper-slide-active) {
    .product-card {
      @apply flex flex-col rounded-xl;

      &__image {
        display: flex;
        justify-content: center;
        height: 50%;
      }
      &__content {
        padding: 30px;
        padding-top: 0;
        height: 50%;
      }
    }
  }

  .swiper-slide-prev,
  .swiper-slide-active {
    .product-card {
      @apply flex flex-row min-h-full justify-between m-0 rounded-xl;

      @media (max-width: 767px) {
        @apply flex-col;
      }

      &__image {
        display: flex;
        justify-content: center;
        @apply w-1/2;

        @media (max-width: 767px) {
          @apply w-full h-1/2;
        }

        & img {
          @media (min-width: 767px) {
            height: 475px;
          }
        }
      }

      &__content {
        height: 50%;
        padding: 20px;
        padding-top: 0;

        @media (min-width: 767px) {
          @apply w-1/2;
        }

        @media (min-width: 1200px) {
          padding: 30px;
          padding-top: 0;
        }
      }
    }
  }
}
</style>
